// MARK://dhk8/pos/hooks/usePrinting
// react-query hooks over the printing client (dhk8-pos-printing). tenantId/branchId
// come from the session (cookie + auth store).
//
// Print path: POST /v1/print/jobs encola con `rendered` (cloud renderiza). El
// edge agent (DocketOS orchestrator cloud-puller, F6.1) consume QUEUED y manda
// a la ESC/POS. Default `edge` — el papel sale por el Pi y NO se abre el
// diálogo del browser. Para deployments sin edge (ej. dev kiosko en mac sin
// Pi) set VITE_PRINT_MODE='browser' para forzar window.print().
//
// VITE_PRINT_MODE='bridge' habla directo al NintaiGO Bridge en el mismo
// equipo (loopback http://127.0.0.1:9101). Usado en SAVIs Caja 1 — el NUC
// corre Chrome + Bridge + impresora USB en el mismo hardware. El cloud
// enqueueJob sigue corriendo en paralelo como audit-only (best-effort) y
// como fallback si el Bridge no responde en BRIDGE_TIMEOUT_MS.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listTemplates, listJobs, enqueueJob, reprintJob,
  type TemplateKind, type PrintJobStatus,
} from '../lib/clients/printing.js'
import { getTenantId } from '../lib/api.js'
import { useAuthStore } from '../stores/auth-store.js'
import { useTenantId } from './useTenantId.js'
import { useOrderStore, type OrderDto, type OrderItem, type RegisteredPayment } from '../stores/order-store.js'
import { printHtml } from '../lib/print/printHtml.js'
import { renderReceipt, renderPrecuenta, renderKitchen } from '../lib/print/templates.js'

// Print mode resolution: localStorage override beats VITE env. Operators on
// a Bridge-equipped machine (ej. NUC SAVIs Caja 1) set this once in DevTools:
//   localStorage.setItem('dhk8_pos_print_mode', 'bridge')
// Other tenants/devices keep the deployed default (edge), so we don't need a
// separate Docker image variant. Valid values: 'edge' | 'browser' | 'bridge'.
function resolvePrintMode(): 'edge' | 'browser' | 'bridge' {
  try {
    if (typeof window !== 'undefined') {
      const v = window.localStorage.getItem('dhk8_pos_print_mode')
      if (v === 'edge' || v === 'browser' || v === 'bridge') return v
    }
  } catch { /* SSR or storage blocked — fall through */ }
  const env = import.meta.env.VITE_PRINT_MODE
  if (env === 'browser') return 'browser'
  if (env === 'bridge')  return 'bridge'
  return 'edge'
}
function resolveBridgePrinter(): string {
  try {
    if (typeof window !== 'undefined') {
      const v = window.localStorage.getItem('dhk8_pos_bridge_printer')
      if (v) return v
    }
  } catch { /* noop */ }
  return (import.meta.env.VITE_BRIDGE_PRINTER as string | undefined) ?? 'EPSON TM-T20II Receipt5 (Copiar 1)'
}
function resolveBridgeUrl(): string {
  try {
    if (typeof window !== 'undefined') {
      const v = window.localStorage.getItem('dhk8_pos_bridge_url')
      if (v) return v
    }
  } catch { /* noop */ }
  return (import.meta.env.VITE_BRIDGE_URL as string | undefined) ?? 'http://127.0.0.1:9101'
}

const PRINT_MODE = resolvePrintMode()
const BROWSER_MODE = PRINT_MODE === 'browser'
const BRIDGE_MODE  = PRINT_MODE === 'bridge'
const BRIDGE_TIMEOUT_MS = 2500

async function maybeBrowserPrint(opts: Parameters<typeof printHtml>[0]): Promise<void> {
  if (!BROWSER_MODE) return
  await printHtml(opts)
}

// ── Bridge wiring ────────────────────────────────────────────────────────────

interface CanonicalReceiptLine { text: string; align?: 'left'|'center'|'right'; bold?: boolean; size?: 'normal'|'double' }
interface CanonicalReceiptItem  {
  qty: number
  name: string
  amountCents: number
  /** Lane #160 — modifiers + notas indented bajo cada línea. El bridge .NET
   *  (EscPosRenderer.cs:154 "v0.2 — emit indented SubLines") las imprime con
   *  2 espacios de indent. Lane #160.C (2026-05-26) — el bridge espera
   *  `List<TextLine>` (objetos con `text`), NO `string[]`. Mandar strings
   *  causaba 400 invalid_json y los tickets de PRECUENTA no salían. */
  subLines?: CanonicalReceiptLine[]
}
interface CanonicalReceiptTotal { label: string; amountCents: number; bold?: boolean }
interface CanonicalReceipt {
  encoding: 'cp850'
  width: 42
  header: CanonicalReceiptLine[]
  items: CanonicalReceiptItem[]
  totals: CanonicalReceiptTotal[]
  footer: CanonicalReceiptLine[]
  qr?: { data: string; size: number }
  cashdrawer?: { pin: 2 | 5 }
  cut: 'partial' | 'full'
}

function lineItemAmountCents(it: OrderItem): number {
  if (typeof it.totalCents === 'number') return it.totalCents
  return it.unitPriceCents * (it.quantity ?? 1)
}

function orderHasCashPayment(payments: readonly RegisteredPayment[]): boolean {
  return payments.some((p) => p.method === 'CASH')
}

function buildReceipt(opts: {
  order: OrderDto
  header: { businessName?: string; branchName?: string; rfc?: string }
  payments?: readonly RegisteredPayment[]
  variant: 'RECEIPT' | 'PRECUENTA'
}): CanonicalReceipt {
  const { order, header, payments = [], variant } = opts
  const businessName = header.businessName ?? 'NintaiGO'
  const titleLines: CanonicalReceiptLine[] = [
    { text: businessName, align: 'center', bold: true, size: 'double' },
  ]
  if (header.branchName) titleLines.push({ text: header.branchName, align: 'center' })
  if (header.rfc)        titleLines.push({ text: `RFC: ${header.rfc}`, align: 'center' })
  // Title prominente: en PRECUENTA usamos doble alto+bold para que el cliente
  // vea inmediatamente que NO es comprobante fiscal. En RECEIPT usamos folio
  // normal (la cabecera del ticket ya tiene el branding del negocio arriba).
  if (variant === 'PRECUENTA') {
    titleLines.push({ text: 'PRECUENTA', align: 'center', bold: true, size: 'double' })
  } else {
    titleLines.push({ text: `Folio ${order.folio ?? order.id.slice(-6)}`, align: 'center' })
  }

  // Items con sub-líneas: cada modifier + nota se imprime indentada bajo el
  // nombre del producto. El bridge .NET ya soporta subLines desde v0.2 del
  // EscPosRenderer (Lane #160 commit). Lane #176-bridge-printout: incluimos
  // modifiers con priceDeltaCents si existe (e.g. "+ Leche deslactosada $5.00")
  // y notas como "★ sin cebolla". Si el item no tiene ni modifiers ni notas,
  // subLines queda undefined y el bridge imprime sólo la línea principal.
  const items: CanonicalReceiptItem[] = order.items.map((it) => {
    // Lane #160.C — subLines tipadas como TextLine (objetos), no string[].
    // El bridge .NET 0.2.0 deserializa `Item.SubLines: List<TextLine>` y
    // rechazaba con 400 invalid_json cuando le mandábamos strings sueltos.
    const subs: CanonicalReceiptLine[] = []
    const modifiers = (it as { modifiers?: ReadonlyArray<{ name: string; priceDeltaCents?: number }> }).modifiers
    if (modifiers && modifiers.length > 0) {
      for (const m of modifiers) {
        if (m.priceDeltaCents && m.priceDeltaCents > 0) {
          subs.push({ text: `+ ${m.name} ($${(m.priceDeltaCents / 100).toFixed(2)})` })
        } else {
          subs.push({ text: `+ ${m.name}` })
        }
      }
    }
    const notes = (it as { notes?: string | null }).notes
    if (notes && notes.trim()) subs.push({ text: `★ ${notes.trim()}` })
    const base: CanonicalReceiptItem = { qty: it.quantity, name: it.name, amountCents: lineItemAmountCents(it) }
    if (subs.length > 0) base.subLines = subs
    return base
  })

  const totals: CanonicalReceiptTotal[] = [
    { label: 'Subtotal', amountCents: order.subtotalCents },
  ]
  if (order.discountCents > 0) totals.push({ label: 'Descuento', amountCents: -order.discountCents })
  if (order.taxCents > 0)      totals.push({ label: 'IVA',       amountCents: order.taxCents })
  if (order.tipCents > 0)      totals.push({ label: 'Propina',   amountCents: order.tipCents })
  totals.push({ label: 'TOTAL', amountCents: order.totalCents, bold: true })

  // Footer: en PRECUENTA hacemos la leyenda prominente (bold) para que sea
  // visualmente claro que NO es CFDI. RECEIPT mantiene "Gracias por su compra"
  // amistoso porque ya el folio + el QR (si aplica) son la marca de comprobante.
  const footer: CanonicalReceiptLine[] = []
  if (variant === 'PRECUENTA') {
    footer.push({ text: 'NO ES COMPROBANTE FISCAL', align: 'center', bold: true })
    footer.push({ text: 'Pase a caja para cobrar', align: 'center' })
  } else {
    footer.push({ text: 'Gracias por su compra', align: 'center' })
  }
  footer.push({ text: new Date().toLocaleString('es-MX'), align: 'center' })

  const receipt: CanonicalReceipt = {
    encoding: 'cp850', width: 42,
    header: titleLines, items, totals, footer,
    cut: 'partial',
  }
  // Drawer kick: only on RECEIPT (post-payment) and only when at least one
  // payment was CASH. Precuenta and card-only sales must not open the drawer.
  if (variant === 'RECEIPT' && orderHasCashPayment(payments)) {
    receipt.cashdrawer = { pin: 2 }
  }
  return receipt
}

async function postReceiptToBridge(receipt: CanonicalReceipt): Promise<void> {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), BRIDGE_TIMEOUT_MS)
  try {
    const bridgeUrl = resolveBridgeUrl()
    const bridgePrinter = resolveBridgePrinter()
    const url = `${bridgeUrl}/v1/print/receipt?printer=${encodeURIComponent(bridgePrinter)}`
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(receipt),
      signal: ctrl.signal,
    })
    if (!res.ok) {
      const body = await res.text().catch(() => '')
      throw new Error(`Bridge ${res.status}: ${body.slice(0, 200)}`)
    }
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Lane #160-fix — el bridge se intenta SIEMPRE que esté alcanzable. Antes
 * sólo corría cuando `PRINT_MODE === 'bridge'` (env var / localStorage), lo
 * que provocaba que en SAVIs el botón Precuenta no imprimiera porque el
 * default era 'edge' aunque el bridge estuviera vivo. Ahora:
 *
 *   - Si el bridge responde (timeout 2.5s en postReceiptToBridge), imprime.
 *   - Si NO responde (offline, tenant sin bridge), el catch silencia el
 *     error y el flow sigue con browser/edge según corresponda.
 *
 * Para tenants que prefieren NO usar bridge nunca (porque tienen Pi edge
 * o sólo navegador), setear `localStorage.dhk8_pos_bridge_disabled = '1'`.
 * Este es un opt-out explícito, no el default.
 */
/**
 * @returns 'printed' si el bridge confirmó, 'disabled' si el opt-out está activo
 *          o no hay browser/loopback, 'failed' si intentamos pero el bridge no
 *          respondió OK. El caller usa esto para diferenciar el toast — Lane
 *          #160.B (2026-05-26): el silent-catch dejaba al usuario creyendo que
 *          la precuenta salió cuando en realidad el NUC no recibió nada.
 */
async function maybeBridgePrint(
  variant: 'RECEIPT' | 'PRECUENTA',
  order: OrderDto,
  header: ReturnType<typeof getTenantHeader>,
): Promise<'printed' | 'disabled' | 'failed'> {
  try {
    if (typeof window !== 'undefined' && window.localStorage.getItem('dhk8_pos_bridge_disabled') === '1') {
      return 'disabled'
    }
  } catch { /* noop */ }
  const payments = useOrderStore.getState().payments
  const receipt = buildReceipt({ order, header, payments, variant })
  if (receipt.items.length === 0) {
    // Si el closure trajo un order sin líneas (race con sendToKitchen / store
    // stale), no mandamos un papel en blanco al rollo. Lo reportamos para que
    // el caller muestre un toast accionable en lugar de "enviada a impresora".
    // eslint-disable-next-line no-console
    console.warn('[usePrinting] bridge skip — order sin items', { orderId: order.id, variant })
    return 'failed'
  }
  try {
    await postReceiptToBridge(receipt)
    return 'printed'
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[usePrinting] bridge print failed — fallback a browser/edge si está configurado', err)
    return 'failed'
  }
}

function reportEnqueueError(kind: string, err: unknown): null {
  // No queremos romper la UI si el enqueue falla, pero el silent-catch nos cegaba:
  // el botón abría el diálogo del browser, el job nunca llegaba al Pi, y no había
  // rastro en consola. Loggear con stack para que el cliente HTTP del navegador
  // muestre el 4xx/5xx del cloud.
  // eslint-disable-next-line no-console
  console.error(`[usePrinting] enqueue ${kind} failed`, err)
  return null
}

function ctx(): { tenantId: string; branchId: string } {
  const user = useAuthStore.getState().user
  const tenantId = getTenantId()
  if (!tenantId || !user) throw new Error('Sesión no inicializada')
  return { tenantId, branchId: user.branchId }
}

export const printingKeys = {
  templates: (tenantId: string | null, kind?: TemplateKind) =>
    ['printing', 'templates', tenantId ?? 'no-tenant', kind ?? 'all'] as const,
  jobs: (tenantId: string | null, status?: PrintJobStatus) =>
    ['printing', 'jobs', tenantId ?? 'no-tenant', status ?? 'all'] as const,
}

export function usePrintTemplates(kind?: TemplateKind) {
  const tenantId = useTenantId()
  return useQuery({
    queryKey: printingKeys.templates(tenantId, kind),
    queryFn: ({ signal }) => listTemplates({ tenantId: tenantId ?? '', ...(kind ? { kind } : {}) }, signal),
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
    select: (r) => r.items,
  })
}

export function usePrintJobs(status?: PrintJobStatus) {
  const tenantId = useTenantId()
  return useQuery({
    queryKey: printingKeys.jobs(tenantId, status),
    queryFn: ({ signal }) => {
      const { tenantId: t, branchId } = ctx()
      return listJobs({ tenantId: t, branchId, ...(status ? { status } : {}), limit: 25 }, signal)
    },
    enabled: !!tenantId,
    staleTime: 10_000,
    refetchInterval: 15_000, // statuses move as the Edge processes
    select: (r) => r.items,
  })
}

/**
 * Reads the current order from the store. The hooks below print whatever the
 * store has — caller is responsible for making sure the right order is loaded.
 * Falls back to a minimal fixture if no order is present (e.g. for test prints).
 */
function getOrderForPrinting(orderId: string): OrderDto {
  const order = useOrderStore.getState().order
  if (order && order.id === orderId) return order
  // Caller is reprinting an order not in memory (e.g. from /tickets history).
  // Return a placeholder so the template doesn't crash. The audit record in
  // /v1/print/jobs has the real orderId for traceability.
  return { id: orderId, items: [], folio: orderId.slice(-6) } as unknown as OrderDto
}

function getTenantHeader(): { businessName?: string; branchName?: string; rfc?: string } {
  const s = useAuthStore.getState()
  const header: { businessName?: string; branchName?: string; rfc?: string } = {}
  if (s.accountName) header.businessName = s.accountName
  // TODO: surface branch.name + tenant.rfc when those reach the store; the ticket
  // template gracefully omits them when undefined.
  return header
}

export function useEnqueueReceipt() {
  const qc = useQueryClient()
  return useMutation({
    /**
     * Accepts either an order-id (looks up the current order in the store) or a
     * full OrderDto (used by FastBar after a sale closes — the cart store is
     * cleared by then, so we pass the just-closed order directly).
     */
    mutationFn: async (input: string | OrderDto) => {
      const { tenantId, branchId } = ctx()
      const order: OrderDto = typeof input === 'string' ? getOrderForPrinting(input) : input
      const orderId = order.id
      const header = getTenantHeader()
      // Fire the browser print, the bridge print, and the audit POST in
      // parallel — all best-effort. Bridge fails → cloud enqueue is audit.
      // renderReceipt es async (QR generation) — await ANTES de pasar a maybeBrowserPrint.
      const html = await renderReceipt(order, header)
      const [, , job] = await Promise.all([
        maybeBrowserPrint({ html, title: `Ticket ${order.folio ?? orderId}`, paperKind: 'thermal-80mm' }),
        maybeBridgePrint('RECEIPT', order, header),
        enqueueJob({ tenantId, branchId, kind: 'RECEIPT', payload: { orderId } }).catch((err) => reportEnqueueError('RECEIPT', err)),
      ])
      return job
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['printing', 'jobs'] }) },
  })
}

/**
 * Lane #160.B (2026-05-26): el bridge ahora reporta su outcome ('printed' |
 * 'disabled' | 'failed') y lo propagamos al callsite. Antes el catch silencioso
 * dejaba al usuario con el toast "Precuenta enviada a impresora" aunque el NUC
 * no hubiera recibido nada. Si el bridge devuelve 'failed', el caller decide:
 * toast de error + sugerencia de reintento, en lugar de fingir éxito.
 *
 * Re-fetch del store SIEMPRE antes de imprimir: aunque el caller nos pase un
 * OrderDto, lo refrescamos contra `useOrderStore.getState().order` cuando el id
 * coincide. Eso evita el bug de "le piqué a precuenta inmediatamente después
 * de enviar a cocina" — el closure del componente puede tener una versión
 * stale del order si sendToKitchen mutó items en el store entre renders.
 */
export function useEnqueuePrecuenta() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: string | OrderDto) => {
      const { tenantId, branchId } = ctx()
      const initial: OrderDto = typeof input === 'string' ? getOrderForPrinting(input) : input
      // Refrescar contra el store por si el closure es viejo (race con sendToKitchen).
      const fromStore = useOrderStore.getState().order
      const order: OrderDto = fromStore && fromStore.id === initial.id ? fromStore : initial
      const orderId = order.id
      const header = getTenantHeader()
      const html = await renderPrecuenta(order, header)
      const [, bridgeOutcome, job] = await Promise.all([
        maybeBrowserPrint({ html, title: `Precuenta ${order.folio ?? orderId}`, paperKind: 'thermal-80mm' }),
        maybeBridgePrint('PRECUENTA', order, header),
        enqueueJob({ tenantId, branchId, kind: 'PRECUENTA', payload: { orderId } }).catch((err) => reportEnqueueError('PRECUENTA', err)),
      ])
      return { job, bridgeOutcome }
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['printing', 'jobs'] }) },
  })
}

/**
 * Fire-to-kitchen print. Renders a KITCHEN slip for the given order and prints
 * to the device's default thermal. When the station routing layer is wired (v1.1),
 * this will fan out one slip per station with a paper output and filter items.
 * Today: single station, all items on one slip.
 */
export function useFireKitchenPrint() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: string | OrderDto) => {
      const { tenantId, branchId } = ctx()
      const order: OrderDto = typeof input === 'string' ? getOrderForPrinting(input) : input
      const orderId = order.id
      const [, job] = await Promise.all([
        maybeBrowserPrint({ html: renderKitchen(order, { ...getTenantHeader(), stationName: 'COCINA' }), title: `Comanda ${order.folio ?? orderId}`, paperKind: 'thermal-80mm' }),
        enqueueJob({ tenantId, branchId, kind: 'KITCHEN', payload: { orderId } }).catch((err) => reportEnqueueError('KITCHEN', err)),
      ])
      return job
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['printing', 'jobs'] }) },
  })
}

export function useReprintJob() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (jobId: string) => reprintJob(jobId),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['printing', 'jobs'] }) },
  })
}
